import React from 'react';
import { Link } from 'react-router-dom';
import FundWiseLogo from './FundWiseLogo';
import { ArrowLeft, Package, Download, Clock, Globe, Phone, Mail, Shield } from 'lucide-react';

const ShippingPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center">
              <FundWiseLogo size="md" variant="default" />
            </Link>
            <Link 
              to="/" 
              className="flex items-center space-x-2 text-slate-600 hover:text-slate-800 transition-colors"
              data-testid="back-to-home"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          {/* Title Header */}
          <div className="flex items-center mb-8">
            <Package className="w-8 h-8 text-blue-600 mr-3" />
            <h1 className="text-4xl font-bold text-slate-900">Shipping & Delivery Policy</h1>
          </div>

          <p className="text-slate-600 mb-8">Effective Date: October 4, 2025 | Last Updated: October 4, 2025</p>

          {/* Digital Delivery Overview */}
          <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-6 mb-8">
            <div className="flex items-center mb-4">
              <Download className="w-6 h-6 text-blue-600 mr-3" />
              <h2 className="text-xl font-bold text-blue-800">100% Digital Delivery - No Physical Shipping Required</h2>
            </div>
            <p className="text-blue-700">
              Portfolio Auditor is a digital software product. All deliveries are made electronically through instant download. 
              There are no physical products to ship, ensuring immediate access to your purchase.
            </p>
          </div>

          {/* Section 1: Service Overview */}
          <section className="space-y-6 mb-8">
            <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">1. Service Overview</h2>
            
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-slate-900">1.1 Product Type</h3>
              <p className="text-slate-700 leading-relaxed">
                Portfolio Auditor provides digital software products for portfolio analysis. Our service includes:
              </p>
              <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                <li><strong className="text-slate-900">Software Bundle:</strong> Desktop application for Windows PC using Microsoft Power BI</li>
                <li><strong className="text-slate-900">Digital License Key:</strong> Unique activation key for software authentication</li>
                <li><strong className="text-slate-900">Documentation:</strong> Installation guides and user manuals (PDF format)</li>
                <li><strong className="text-slate-900">Support Access:</strong> Technical support and customer service</li>
              </ul>
            </div>
          </section>

          {/* Section 2: Delivery Process */}
          <section className="space-y-6 mb-8">
            <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">2. Delivery Process</h2>
            
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-slate-900">2.1 Instant Digital Delivery</h3>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <Clock className="w-5 h-5 text-green-600 mr-2" />
                  <h4 className="text-green-800 font-semibold">Immediate Access Upon Payment</h4>
                </div>
                <p className="text-green-700 text-sm">
                  For both Indian and International customers, software bundle and license key are available for download 
                  immediately after successful payment confirmation. No waiting period required.
                </p>
              </div>
              
              <h4 className="text-lg font-semibold text-slate-900 mt-4">Delivery Steps:</h4>
              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-1">1</div>
                  <div>
                    <p className="font-semibold text-slate-900">Payment Processing</p>
                    <p className="text-slate-600 text-sm">Secure payment verification through Razorpay gateway</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-1">2</div>
                  <div>
                    <p className="font-semibold text-slate-900">License Generation</p>
                    <p className="text-slate-600 text-sm">Unique license key created and assigned to your account</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-1">3</div>
                  <div>
                    <p className="font-semibold text-slate-900">Dashboard Access</p>
                    <p className="text-slate-600 text-sm">Download links and license key available in your account dashboard</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-1">4</div>
                  <div>
                    <p className="font-semibold text-slate-900">Installation & Activation</p>
                    <p className="text-slate-600 text-sm">Download software, install on PC, and activate using provided license key</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-slate-900">2.2 Delivery Confirmation</h3>
              <p className="text-slate-700 leading-relaxed">
                Delivery is considered complete when:
              </p>
              <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                <li>Download links are made available in your account dashboard</li>
                <li>License key is generated and displayed</li>
                <li>Confirmation email is sent to your registered email address</li>
                <li>Software files are accessible for download</li>
              </ul>
            </div>
          </section>

          {/* Section 3: Geographic Coverage */}
          <section className="space-y-6 mb-8">
            <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">3. Geographic Coverage</h2>
            
            <div className="space-y-4">
              <div className="flex items-center mb-4">
                <Globe className="w-6 h-6 text-blue-600 mr-3" />
                <h3 className="text-xl font-semibold text-slate-900">3.1 Worldwide Digital Delivery</h3>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 mb-2">🇮🇳 Indian Customers</h4>
                  <ul className="text-blue-700 text-sm space-y-1">
                    <li>• Instant download access</li>
                    <li>• INR pricing with inclusive taxes</li>
                    <li>• Local payment methods supported</li>
                    <li>• Indian business hours support</li>
                  </ul>
                </div>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-800 mb-2">🌍 International Customers</h4>
                  <ul className="text-green-700 text-sm space-y-1">
                    <li>• Global instant access</li>
                    <li>• Multi-currency support</li>
                    <li>• International payment gateways</li>
                    <li>• English language support</li>
                  </ul>
                </div>
              </div>
              
              <p className="text-slate-700 leading-relaxed">
                <strong>No Geographic Restrictions:</strong> As a digital product, Portfolio Auditor can be delivered to any location worldwide 
                where internet access is available. There are no shipping restrictions, customs clearance, or international delivery delays.
              </p>
            </div>
          </section>

          {/* Section 4: Delivery Timeframes */}
          <section className="space-y-6 mb-8">
            <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">4. Delivery Timeframes</h2>
            
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <table className="min-w-full border border-slate-200 rounded-lg">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="border border-slate-200 px-4 py-3 text-left font-semibold">Subscription Type</th>
                      <th className="border border-slate-200 px-4 py-3 text-left font-semibold">Delivery Time</th>
                      <th className="border border-slate-200 px-4 py-3 text-left font-semibold">Access Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-slate-200 px-4 py-3 font-semibold text-green-700">Beta (Free Trial)</td>
                      <td className="border border-slate-200 px-4 py-3">Instant (0 seconds)</td>
                      <td className="border border-slate-200 px-4 py-3">{process.env.REACT_APP_BETA_PERIOD || '1 month'}</td>
                    </tr>
                    <tr className="bg-slate-50">
                      <td className="border border-slate-200 px-4 py-3 font-semibold text-blue-700">Monthly Plan (₹{process.env.REACT_APP_INDIVIDUAL_PRICE || '99'}/month)</td>
                      <td className="border border-slate-200 px-4 py-3">Instant (after payment)</td>
                      <td className="border border-slate-200 px-4 py-3">30 Days (renewable)</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-200 px-4 py-3 font-semibold text-purple-700">Family (₹{process.env.REACT_APP_FAMILY_PRICE || '199'}/month)</td>
                      <td className="border border-slate-200 px-4 py-3">Instant (after payment)</td>
                      <td className="border border-slate-200 px-4 py-3">30 Days (renewable)</td>
                    </tr>
                    <tr className="bg-slate-50">
                      <td className="border border-slate-200 px-4 py-3 font-semibold text-orange-700">Yearly Plan (₹{process.env.REACT_APP_ANNUAL_PRICE || '699'}/year)</td>
                      <td className="border border-slate-200 px-4 py-3">Instant (after payment)</td>
                      <td className="border border-slate-200 px-4 py-3">365 Days</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* Section 5: Technical Requirements */}
          <section className="space-y-6 mb-8">
            <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">5. Technical Requirements for Delivery</h2>
            
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-slate-900">5.1 Customer Requirements</h3>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="text-yellow-800 font-semibold mb-2">⚠️ Prerequisites for Successful Delivery</h4>
                <ul className="text-yellow-700 text-sm space-y-1">
                  <li>• Stable internet connection for download (minimum 10 MB available)</li>
                  <li>• Windows PC/Laptop (Mac support in development)</li>
                  <li>• Microsoft Power BI Desktop (free download from Microsoft)</li>
                  <li>• Valid email address for account creation and notifications</li>
                  <li>• Browser with JavaScript enabled for dashboard access</li>
                </ul>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-slate-900">5.2 Download Specifications</h3>
              <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                <li><strong className="text-slate-900">File Size:</strong> Approximately 15-25 MB (compressed software bundle)</li>
                <li><strong className="text-slate-900">Format:</strong> ZIP archive containing .pbix files and documentation</li>
                <li><strong className="text-slate-900">Download Method:</strong> Direct download links from secure servers</li>
                <li><strong className="text-slate-900">Download Attempts:</strong> Unlimited downloads during subscription period</li>
              </ul>
            </div>
          </section>

          {/* Section 6: Failed Delivery & Support */}
          <section className="space-y-6 mb-8">
            <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">6. Failed Delivery & Customer Support</h2>
            
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-slate-900">6.1 Common Delivery Issues</h3>
              <div className="space-y-3">
                <div className="border border-slate-200 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">Payment Processing Delays</h4>
                  <p className="text-slate-600 text-sm">
                    If payment verification takes longer than expected, delivery may be delayed by 5-10 minutes. 
                    Contact support if access is not granted within 15 minutes of payment confirmation.
                  </p>
                </div>
                
                <div className="border border-slate-200 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">Download Issues</h4>
                  <p className="text-slate-600 text-sm">
                    Poor internet connectivity or browser issues may interrupt downloads. 
                    All download links remain active throughout your subscription period for re-downloading.
                  </p>
                </div>
                
                <div className="border border-slate-200 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">License Activation Problems</h4>
                  <p className="text-slate-600 text-sm">
                    If license key activation fails, verify internet connection and contact support. 
                    Each license key can be activated on one PC at a time.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-slate-900">6.2 Customer Support for Delivery Issues</h3>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <Shield className="w-5 h-5 text-blue-600 mr-2" />
                  <h4 className="text-slate-800 font-semibold">Portfolio Auditor Support Team</h4>
                </div>
                <p className="text-slate-700 text-sm mb-3">
                  Our support team is available to resolve any delivery-related issues. Portfolio Auditor team is committed to ensuring successful delivery of all digital products.
                </p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <Phone className="w-4 h-4 text-green-600 mr-2" />
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">Phone Support</p>
                      <p className="text-slate-600 text-sm">+91 9629685970</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <Mail className="w-4 h-4 text-blue-600 mr-2" />
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">Email Support</p>
                      <p className="text-slate-600 text-sm">{process.env.REACT_APP_SUPPORT_EMAIL || 'support@portfolioauditor.com'}</p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <p className="text-slate-600 text-xs">
                    <strong>Support Hours:</strong> Monday to Friday, 9:00 AM - 6:00 PM IST | 
                    <strong> Response Time:</strong> Within 24 hours for email, immediate for phone during business hours
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 7: Refunds & Returns */}
          <section className="space-y-6 mb-8">
            <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">7. Digital Product Returns & Refunds</h2>
            
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-slate-900">7.1 Return Policy for Digital Products</h3>
              <p className="text-slate-700 leading-relaxed">
                As Portfolio Auditor is a digital software product that is delivered instantly and cannot be "returned" in the traditional sense, 
                we offer a comprehensive refund policy instead:
              </p>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="text-green-800 font-semibold mb-2">✅ {process.env.REACT_APP_BETA_DURATION?.split('-')[0] || '7'}-Day Money-Back Guarantee</h4>
                <p className="text-green-700 text-sm mb-2">We offer a full refund within {process.env.REACT_APP_BETA_DURATION?.split(' ')[0] || '7'} days of purchase if:</p>
                <ul className="text-green-700 text-sm space-y-1 ml-4">
                  <li>• Software fails to install or function on compatible system</li>
                  <li>• Technical issues prevent proper analysis functionality</li>
                  <li>• Service not delivered as described in our documentation</li>
                </ul>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="text-red-800 font-semibold mb-2">❌ Non-Refundable Situations</h4>
                <ul className="text-red-700 text-sm space-y-1 ml-4">
                  <li>• User dissatisfaction with analysis results after successful delivery</li>
                  <li>• Change of mind after software has been successfully activated</li>
                  <li>• User error in system requirements or usage</li>
                  <li>• Requests made after {process.env.REACT_APP_BETA_DURATION?.split(' ')[0]?.toLowerCase() || '7'}-day guarantee period</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 8: Liability & Limitations */}
          <section className="space-y-6 mb-8">
            <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">8. Delivery Liability & Limitations</h2>
            
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-slate-900">8.1 Service Commitment</h3>
              <p className="text-slate-700 leading-relaxed">
                Portfolio Auditor team commits to successful delivery of digital products but is not liable for issues beyond our control, including:
              </p>
              <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                <li>Customer's internet connectivity or bandwidth limitations</li>
                <li>Customer's system compatibility or technical requirements</li>
                <li>Third-party payment gateway delays or failures</li>
                <li>Force majeure events affecting our servers or infrastructure</li>
              </ul>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-slate-900">8.2 Delivery Guarantee</h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-700 text-sm">
                  <strong>Our Promise:</strong> We guarantee that digital products will be made available for download within 5 minutes 
                  of successful payment verification. If delivery fails due to our system issues, we will provide immediate resolution 
                  or offer a full refund at customer's discretion.
                </p>
              </div>
            </div>
          </section>

          {/* Section 9: Contact Information */}
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">9. Contact Information</h2>
            
            <div className="bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-slate-900 mb-4">Get Help with Delivery Issues</h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Direct Contact</h4>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <Phone className="w-4 h-4 text-green-600 mr-2" />
                      <span className="text-slate-700 text-sm">+91 9629685970</span>
                    </div>
                    <div className="flex items-center">
                      <Mail className="w-4 h-4 text-blue-600 mr-2" />
                      <span className="text-slate-700 text-sm">{process.env.REACT_APP_SUPPORT_EMAIL || 'support@portfolioauditor.com'}</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Business Information</h4>
                  <div className="text-slate-700 text-sm space-y-1">
                    <p><strong>Service Name:</strong> Portfolio Auditor</p>
                    <p><strong>Business Type:</strong> Software as a Service (SaaS)</p>
                    <p><strong>Location:</strong> Bangalore, India</p>
                    <p><strong>Website:</strong> {process.env.REACT_APP_COMPANY_WEBSITE || 'www.portfolioauditor.com'}</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-slate-600 text-xs">
                  <strong>Note:</strong> This Shipping & Delivery Policy is designed to comply with Razorpay payment gateway requirements 
                  and Indian e-commerce regulations for digital product delivery. Last updated: October 4, 2025.
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ShippingPolicy;